import { P2GBox, P2GDivider, P2GLink, P2GLinkGroup, P2GGrid } from '@parcel2go/core-ui';
import { Fragment, useContext, useMemo } from 'react';
import QuickSelectSection from './QuickSelectSection';
import { SmartSendItemsCache } from '../../../lib/cache/SmartSendItemsCache';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  ToggleSelectionItemsAction,
  ToggleSelectionSourceItemsAction,
  ToggleSelectionProviderItemsAction
} from '../../../lib/context/SmartSendContextModels';
import { SelectionOption } from './SelectionOption';
import { SelectionOptionGroup } from './SelectionOptionGroup';
import GenerateQuickSelectOptions from './GenerateQuickSelectOptions';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import useProviderName from '../../../hooks/use-provider-name';

interface props {
  isXs: boolean;
  selected: number;
  restrictToSold: boolean;
  filterKey: string;
}

const sections = [EventSections.SmartSend, EventSections.QuickSelect];

const QuickSelect = (props: props) => {
  const ctx = useContext(SmartSendContext);
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const providerName = useProviderName();

  const options: SelectionOptionGroup[] = useMemo(
    () =>
      GenerateQuickSelectOptions(
        SmartSendItemsCache,
        props.restrictToSold,
        ctx.state.filters[props.filterKey].filter,
        providerName
      ),
    [props.selected, props.restrictToSold, ctx.state.filters[props.filterKey], providerName]
  );

  const setToggle = (type: string, val: boolean) =>
    ctx.dispatch(new ToggleSelectionItemsAction(props.filterKey, type, val));

  const setToggleSource = (source: string, val: boolean) =>
    ctx.dispatch(new ToggleSelectionSourceItemsAction(props.filterKey, source, val));

  const setToggleService = (provider: string, val: boolean) =>
    ctx.dispatch(new ToggleSelectionProviderItemsAction(props.filterKey, provider, val));

  const toggle = (o: SelectionOption, v: boolean) => {
    if (o.provider) {
      setToggleService(o.provider, v);
      track({
        type: 'selection_changed',
        payload: GenerateBaseAnalyticsPayload(sections, {
          surface: 'quick-select',
          action: 'toggle-provider',
          affectedItemCount: props.selected,
          provider: o.provider.toLowerCase(),
          value: v
        })
      });
    } else if (o.source) {
      track({
        type: 'selection_changed',
        payload: GenerateBaseAnalyticsPayload(sections, {
          surface: 'quick-select',
          action: 'toggle-source',
          affectedItemCount: props.selected,
          source: o.source.toLowerCase(),
          value: v
        })
      });
      setToggleSource(o.source, v);
    } else {
      track({
        type: 'selection_changed',
        payload: GenerateBaseAnalyticsPayload(sections, {
          surface: 'quick-select',
          action: 'toggle',
          affectedItemCount: props.selected,
          value: v
        })
      });
      setToggle(o.action, v);
    }
  };

  const toggleAll = (v: boolean) => {
    track({
      type: 'selection_changed',
      payload: GenerateBaseAnalyticsPayload(sections, {
        surface: 'quick-select',
        action: v ? 'select-all' : 'clear',
        affectedItemCount: props.selected,
        value: v
      })
    });
    setToggle('TOGGLE_ALL', v);
  };

  return (
    <P2GGrid container spacing={1} direction="column">
      {options.map((g) => (
        <Fragment key={g.name}>
          <P2GGrid item>
            <QuickSelectSection group={g} toggle={toggle} isXs={props.isXs} />
          </P2GGrid>
          <P2GGrid item>
            <P2GDivider />
          </P2GGrid>
        </Fragment>
      ))}
      <P2GGrid item>
        <P2GBox textAlign="center">
          <P2GLinkGroup
            pt={1}
            links={[
              ctx.state.filters[props.filterKey].summary.selected.length <
                ctx.state.filters[props.filterKey].summary.count && (
                <P2GLink onClick={() => toggleAll(true)}>
                  {t('SELECT_ALL_LINK', { defaultValue: 'Select All' })}
                </P2GLink>
              ),
              props.selected > 0 && (
                <P2GLink onClick={() => props.selected > 0 && toggleAll(false)}>
                  {t('CLEAR_ALL_LINK', { defaultValue: 'Clear All' })}
                </P2GLink>
              )
            ]}
          />
        </P2GBox>
      </P2GGrid>
    </P2GGrid>
  );
};

export default QuickSelect;
